import React, { useState, useContext } from 'react';
import { Box, Text, RadioButtonGroup } from 'grommet';
import { TeamContext } from '../../services/Session/context';
import RoundedToggle from '../../components/RoundedToggle';
import axios from '../../helpers/axios';
import { useDebouncedCallback } from '@helpers/utils/hooksUtils';
import * as Sentry from '@sentry/browser';
import Toast from '@components/Toast';
import { observer } from 'mobx-react-lite';

const TeamPrivacySettings: React.FC = observer(() => {
  const teamStore = useContext(TeamContext);
  const [error, setError] = useState<null | string>(null);
  const [showEditSuccess, setShowEditSuccess] = useState(false);

  const isBBSB =
    teamStore.sport.toLowerCase() === 'softball' ||
    teamStore.sport.toLowerCase() === 'baseball';

  const displayOptions = [
    { label: 'Willie Mays', value: 40 },
    { label: 'Willie M.', value: 30 },
    { label: 'W. Mays', value: 20 },
    { label: 'W.M.', value: 10 },
    { label: "Don't display", value: 0 },
  ];

  const getNameStyleValue = () => {
    if (
      teamStore.embedNameFirstLevel === 2 &&
      teamStore.embedNameLastLevel === 2
    ) {
      return displayOptions[0].value;
    }
    if (
      teamStore.embedNameFirstLevel === 2 &&
      teamStore.embedNameLastLevel === 1
    ) {
      return displayOptions[1].value;
    }
    if (
      teamStore.embedNameFirstLevel === 1 &&
      teamStore.embedNameLastLevel === 2
    ) {
      return displayOptions[2].value;
    }
    if (
      teamStore.embedNameFirstLevel === 1 &&
      teamStore.embedNameLastLevel === 1
    ) {
      return displayOptions[3].value;
    }
    return displayOptions[4].value;
  };

  const debouncedUpdateTeamSettings = useDebouncedCallback(
    (newTeam: unknown) => {
      axios
        .put(`/api/team/${teamStore.id}`, {
          teamInfo: newTeam,
          isEditPrivacy: true,
        })
        .then((x) => {
          teamStore.setPrivacySettings(x.data);
          setShowEditSuccess(true);
        })
        .catch((respError) => {
          Sentry.captureException(respError);
          if (respError.response && respError.response.data.message) {
            setError(respError.response.data.message);
          }
        });
    },
    500,
    { leading: false, trailing: true }
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateTeam = (newTeam: any) => {
    debouncedUpdateTeamSettings(newTeam);
  };

  return (
    <Box>
      {error && <Text color="status-error">{error}</Text>}
      {showEditSuccess && (
        <Toast
          label="Settings saved"
          background="status-ok"
          duration={3000}
          onClose={() => setShowEditSuccess(false)}
        />
      )}
      <Box pad={{ horizontal: '16px' }}>
        <Box
          pad={{ vertical: '16px' }}
          gap="16px"
          border={{ side: 'bottom', color: 'light-1' }}
        >
          <Text weight={'bold'} size="small">
            Player Name Display Style
          </Text>
          <Box direction="row" gap="16px" align="center">
            <Box gap="4px">
              <Text weight={'bold'}>There are players under the age of 13</Text>
              <Text>
                If a team has any players under the age of 13, we do not display
                full names.
              </Text>
            </Box>
            <RoundedToggle
              checked={teamStore.isAge13Plus}
              onChange={(x) => {
                const settings = teamStore.fetchPrivacySettings;
                if (x.target.checked) {
                  settings.embedNameFirstLevel = 2;
                  settings.embedNameLastLevel = 1;
                }
                updateTeam({ ...settings, isAge13Plus: x.target.checked });
              }}
            />
          </Box>
          <Box gap="8px">
            <Text weight={'bold'}>Display Style</Text>
            <RadioButtonGroup
              name="radio"
              value={getNameStyleValue()}
              options={displayOptions.filter((option) => {
                if (!teamStore.isAge13Plus && option.value === 40) {
                  return false;
                }
                if (!teamStore.isAge13Plus && option.value === 20) {
                  return false;
                }
                return true;
              })}
              onChange={({ target: { value } }) => {
                const newTeam = { ...teamStore.fetchPrivacySettings };
                if (value === '40') {
                  newTeam.embedNameFirstLevel = 2;
                  newTeam.embedNameLastLevel = 2;
                } else if (value === '30') {
                  newTeam.embedNameFirstLevel = 2;
                  newTeam.embedNameLastLevel = 1;
                } else if (value === '20') {
                  newTeam.embedNameFirstLevel = 1;
                  newTeam.embedNameLastLevel = 2;
                } else if (value === '10') {
                  newTeam.embedNameFirstLevel = 1;
                  newTeam.embedNameLastLevel = 1;
                } else if (value === '0') {
                  newTeam.embedNameFirstLevel = 0;
                  newTeam.embedNameLastLevel = 0;
                  newTeam.isNameOnPublicWebsite = false;
                }
                updateTeam(newTeam);
              }}
            />
          </Box>
        </Box>
        <Box
          pad={{ vertical: '16px' }}
          gap="16px"
          border={{ side: 'bottom', color: 'light-1' }}
        >
          <Text weight={'bold'} size="small">
            Player Privacy
          </Text>
          <Box gap="16px" flex fill="horizontal">
            <Box direction="row" justify="between" align="center" gap="16px">
              <Text>Show Player Names on Team Page</Text>
              <RoundedToggle
                checked={teamStore.isNameOnPublicWebsite}
                onChange={(x) => {
                  updateTeam({
                    ...teamStore.fetchPrivacySettings,
                    isNameOnPublicWebsite: x.target.checked,
                  });
                }}
              />
            </Box>
            <Box direction="row" justify="between" align="center" gap="16px">
              <Text>
                Show player profile pictures on team page when available
              </Text>
              <RoundedToggle
                checked={teamStore.isEmbedImagePublicWebsite}
                onChange={(x) => {
                  updateTeam({
                    ...teamStore.fetchPrivacySettings,
                    isEmbedImagePublicWebsite: x.target.checked,
                  });
                }}
              />
            </Box>
            <Box direction="row" justify="between" align="center" gap="16px">
              <Text>
                Show player profile pictures on livestream when available
              </Text>
              <RoundedToggle
                checked={teamStore.isEmbedImageVideo}
                onChange={(x) => {
                  updateTeam({
                    ...teamStore.fetchPrivacySettings,
                    isEmbedImageVideo: x.target.checked,
                  });
                }}
              />
            </Box>
            {!isBBSB && (
              <Box direction="row" justify="between" align="center" gap="16px">
                <Text>
                  Show unclaimed players&apos; clips to public (recommended)
                </Text>
                <RoundedToggle
                  checked={teamStore.publicPublishLevel === 10}
                  onChange={(x) => {
                    const publicPublishLevel = x.target.checked ? 10 : 5;
                    updateTeam({
                      ...teamStore.fetchPrivacySettings,
                      publicPublishLevel,
                    });
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          pad={{ vertical: '16px' }}
          gap="16px"
          border={{ side: 'bottom', color: 'light-1' }}
        >
          <Text weight={'bold'} size="small">
            Live Highlights
          </Text>
          <Box direction="row" justify="between" align="center" gap="16px">
            <Text>Send LIVE player highlights via text message</Text>
            <RoundedToggle
              checked={teamStore.isNotifyCommunityTeamPlayerUpdates}
              onChange={(x) => {
                updateTeam({
                  ...teamStore.fetchPrivacySettings,
                  isNotifyCommunityTeamPlayerUpdates: x.target.checked,
                });
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default TeamPrivacySettings;
